import { useState, useCallback } from 'react';
import type { ComputedColumn } from './types';
import { useTheme } from '@simplywallst/ui-core';
import { VisxText } from '@/components/VisxText';

interface Props {
  data: ComputedColumn[];
  height: number;
  showColumnKey?: boolean;
  hoveringColumnKey?: string;
  onHover?: (key: string) => void;
}

export const StackBars = ({
  data,
  showColumnKey,
  hoveringColumnKey,
  onHover,
  height,
}: Props) => {
  const theme = useTheme();
  const offsetTop = theme.x5;
  const offsetBottom = theme.x5;
  const columnPadding = theme.x1;

  const [hoveringKeyState, setHoveringkeyState] = useState<string>(
    data?.[0]?.key
  );

  const hoveringKey = hoveringColumnKey || hoveringKeyState;

  const hoverHandler = useCallback(
    (key: string) => {
      if (!hoveringColumnKey) {
        setHoveringkeyState(key);
      }
      onHover?.(key);
    },
    [hoveringColumnKey, onHover]
  );

  return data.map((column) => {
    return (
      <g key={column.key}>
        <rect
          style={{ cursor: 'pointer' }}
          x={column.x}
          y="0"
          width={column.width}
          height={showColumnKey ? height + offsetBottom : height}
          fill={theme.color.textSolid}
          fillOpacity={hoveringKey === column.key ? 0.1 : 0}
          onMouseEnter={() => hoverHandler(column.key)}
        />
        {column.bars.map(
          ({ key, color, height: barHeight, width: barWidth, x, y }) => {
            return (
              <rect
                style={{ cursor: 'pointer' }}
                key={`bar-${column.key}-${key}`}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={color}
                onMouseEnter={() => hoverHandler(column.key)}
              />
            );
          }
        )}
        {column.formattedValue && (
          <VisxText
            typography="xSmallStrong"
            y={column.top}
            x={column.x + columnPadding}
            dx={0}
            dy={-offsetTop / 2}
            lineHeight="1.5em"
            width={column.width}
            verticalAnchor="middle"
            fill={theme.color.textSoft}
          >
            {column.formattedValue}
          </VisxText>
        )}
        {showColumnKey && (
          <VisxText
            typography="xSmall"
            y={height + offsetBottom}
            x={column.x + columnPadding}
            dy={-offsetBottom / 2}
            lineHeight="1.5em"
            width={column.width - columnPadding * 2}
            fill={theme.color.textSolid}
            verticalAnchor="middle"
            textAnchor="start"
            dx={0}
          >
            {column.key}
          </VisxText>
        )}
      </g>
    );
  });
};
